import React, { useState, useEffect, useMemo, useRef } from 'react';
import { sendRequest, API_ROOT } from '../../supplementary_components/requests';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import { ListViewFooter } from '../../general_components/content_management/list_view/listViewFooter';
import { FaChevronDown, FaChevronUp, FaChevronLeft, FaChevronRight, FaRegCalendarAlt } from "react-icons/fa";
import { HiArrowNarrowDown, HiArrowNarrowUp } from 'react-icons/hi';
import { GrossColorGenerator, RPMColorGenerator } from '../../main_board_components/teamCard/truckCard/truckCardComponents/grossSections';
import { priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import{ Link } from 'react-router-dom';
import styles from './reports.module.scss';
import { CalendarPicker, useCloseOnOutsidePress } from '../../general_components/content_management/reusableItems/calendar';
import { ListView } from '../../general_components/content_management/list_view/listView';
import { FaRegFileLines } from "react-icons/fa6";
import { IoSettingsSharp } from 'react-icons/io5';
import { swapSVGWhileProcessing } from '../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection';
import { flag } from '../../supplementary_components/userDataDelegate';
import { addPopup } from '../../supplementary_components/store';
import { getVisibleReportColumns, isLegacyTruckLoadColumns, orderKeysByCatalog } from './reportColumnSettings';
import { ReportColumnSettingsPopup } from './reportColumnSettingsPopup';

export const WeekSelector = ({ children, className = '', ...rest }) => (
    <div {...rest} className={`${styles.weekSelector}${className ? ' ' + className : ''}`}>
        {children}
    </div>
);

export const TimeframeSelector = ({ children, className = '', ...rest }) => (
    <div {...rest} className={`${styles.timeframeSelector}${className ? ' ' + className : ''}`}>
        {children}
    </div>
);

const TIMEFRAME_OPTIONS = [
    {key:'weekly', label:'Weekly'},
    {key:'biweekly', label:'Bi-Weekly'},
    {key:'monthly', label:'Monthly'},
    {key:'quarterly', label:'Quarterly'},
    {key:'yearly', label:'Yearly'},
];

export const ReportTimeframeSelector = ({selectedTimeframe, onChange, allowCustomTimeframe}) => {
    const showCustom = allowCustomTimeframe !== false;
    const options = showCustom
        ? [...TIMEFRAME_OPTIONS, {key:'custom', label:'Custom'}]
        : TIMEFRAME_OPTIONS;
    return (
        <TimeframeSelector className="non-selectable">
            {options.map((option)=>(
                <button
                    key={option.key}
                    type="button"
                    className={selectedTimeframe === option.key ? styles.active : ''}
                    onClick={()=>onChange(option.key)}
                >
                    {option.label}
                </button>
            ))}
        </TimeframeSelector>
    );
};

const stripTime = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export const ClickableItem = (props) => <Link {...props} className={styles.clickableItem} />;

const normalizeReportHeader = (item) => typeof item === 'string' ? {label: item} : item;

const compareReportValues = (valA, valB, desc) => {
    if (valA == null && valB == null) return 0;
    if (valA == null) return 1;
    if (valB == null) return -1;

    const cleanNumber = (value) => {
        if (typeof value === 'number') return value;
        if (typeof value !== 'string') return NaN;
        const numeric = Number(value.replace(/[$,%\s,]/g, ''));
        return Number.isNaN(numeric) ? NaN : numeric;
    };
    const numA = cleanNumber(valA);
    const numB = cleanNumber(valB);

    if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
        return desc ? numB - numA : numA - numB;
    }

    const comparison = String(valA).localeCompare(String(valB), undefined, {
        numeric: true,
        sensitivity: 'base'
    });
    return desc ? -comparison : comparison;
};

const getReportSortValue = (report, sortColumn, custoMreportClass) => {
    if (!sortColumn) return null;
    if (typeof sortColumn.methodToObtainRawVal === 'function') {
        try {
            return sortColumn.methodToObtainRawVal(report);
        } catch (error) {
            return null;
        }
    }

    const key = sortColumn.rawValKey || sortColumn.key;
    if (!key) return null;
    if (report && report[key] !== undefined) return report[key];

    if (custoMreportClass) {
        try {
            const preparedReport = new custoMreportClass(report);
            return preparedReport[key];
        } catch (error) {
            return null;
        }
    }

    return null;
};

const getReportRowKey = (report, index) => {
    const key = report?.id || report?.truckNumber || report?.teamName || report?.bookedBy || report?.driverId;
    return key ?? index;
};


export const WeekSelectionWrapper = ({companyData, reportLink, rowType, headerObject, department, custoMreportClass, rowFunction, SubContentPart, subcontentArrayName, predefinedListConf, itemName, startWithPrevWeek, customTableSummary, subDataKey, allowCustomTimeframe, exportToXlsx, sortingAllowed, customAction, columnSettings})=>{
    const [data, setData] = useState(null);
    const [subData, setSubData] = useState(null);
    const [pagonationData, setPagonationData] = useState({"currentPageNumber":0});
    const [error, setError] = useState(null);
    const [updateAvailable, setUpdateAvailable] = useState(true);
    const [selectedTimeframe, setSelectedTimeframe] = useState('weekly');
    const [{startDate, endDate}, setPeriod] = useState(()=>{
        const start = getCurrentWeek(companyData.firstDayOfAWeek, !!startWithPrevWeek);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        return {startDate: start, endDate: end};
    });
    const [customPickerOpen, setCustomPickerOpen] = useState(false);
    const customRangeWrapperRef = useRef(null);
    useCloseOnOutsidePress(customPickerOpen, customRangeWrapperRef, () => setCustomPickerOpen(false));
    const [sortingData, setSortingData] = useState(null);
    const [openRowsResetSignal, setOpenRowsResetSignal] = useState(0);
    const [visibleColumns, setVisibleColumns] = useState(() => (
        columnSettings
            ? getVisibleReportColumns(columnSettings.reportKey, columnSettings.catalog)
            : []
    ));
    const [loadVisibleColumns, setLoadVisibleColumns] = useState(() => (
        columnSettings?.loadColumns
            ? getVisibleReportColumns(columnSettings.loadColumns.reportKey, columnSettings.loadColumns.catalog)
            : []
    ));

    // Computes the {startDate, endDate} for a timeframe relative to a given anchor (defaults to "now").
    // For 'custom' it preserves the existing range so the picker can refine it.
    const rangeForTimeframe = (key, anchorStart = startDate, anchorEnd = endDate) => {
        const now = new Date();
        switch(key){
            case 'weekly': {
                const s = getCurrentWeek(companyData.firstDayOfAWeek, !!startWithPrevWeek);
                const e = new Date(s); e.setDate(e.getDate() + 6);
                return {startDate: s, endDate: e};
            }
            case 'biweekly': {
                // Last two weeks: start = previous week's start, end = current week's end.
                const s = getCurrentWeek(companyData.firstDayOfAWeek, !!startWithPrevWeek);
                s.setDate(s.getDate() - 7);
                const e = new Date(s); e.setDate(e.getDate() + 13);
                return {startDate: s, endDate: e};
            }
            case 'monthly': {
                const s = new Date(now.getFullYear(), now.getMonth(), 1);
                const e = new Date(now.getFullYear(), now.getMonth()+1, 0);
                return {startDate: s, endDate: e};
            }
            case 'quarterly': {
                const qStart = Math.floor(now.getMonth()/3) * 3;
                const s = new Date(now.getFullYear(), qStart, 1);
                const e = new Date(now.getFullYear(), qStart+3, 0);
                return {startDate: s, endDate: e};
            }
            case 'yearly': {
                const s = new Date(now.getFullYear(), 0, 1);
                const e = new Date(now.getFullYear(), 11, 31);
                return {startDate: s, endDate: e};
            }
            case 'custom':
            default:
                return {startDate: anchorStart, endDate: anchorEnd};
        }
    };

    const _seData = (data) => { 
        setUpdateAvailable(false); 
        setData(data.dataset);
        delete data["dataset"]; 
        try {
            if(subDataKey){
                setSubData(data[subDataKey]);
                delete data[subDataKey]; 
            }
        } catch (error) {
        }
        setPagonationData(data);
    }
    useEffect(
        () => {
            if(updateAvailable){
                setError(null);
                setData(null);
                const controller = new AbortController();
                const signal = controller.signal;
                sendRequest('GET', API_ROOT+reportLink+`?pageNumber=${pagonationData.currentPageNumber}&timeframe=${selectedTimeframe}&startDate=${dateToLocalDateString(startDate)}&endDate=${dateToLocalDateString(endDate)}`, signal, _seData, setError, null);
                return () => {
                    // cancel the request before component unmounts
                    controller.abort();
                };
            }
        // eslint-disable-next-line
        }, [updateAvailable]
    );
    const sortedData = useMemo(() => {
        if (!sortingAllowed || predefinedListConf || !sortingData || !Array.isArray(data)) return data;

        return [...data].sort((a, b) => {
            const valA = getReportSortValue(a, sortingData.column, custoMreportClass);
            const valB = getReportSortValue(b, sortingData.column, custoMreportClass);
            return compareReportValues(valA, valB, sortingData.desc);
        });
    }, [data, sortingAllowed, predefinedListConf, sortingData, custoMreportClass]);
    const handleReportSort = (headerItem) => {
        const column = normalizeReportHeader(headerItem);
        const canSort = column?.sortable !== false && (column?.rawValKey || column?.key || column?.methodToObtainRawVal);
        if (!sortingAllowed || !canSort) return;

        const sortKey = column.rawValKey || column.key || column.label;
        setSortingData((prev) => {
            if (!prev || prev.key !== sortKey) {
                return {key: sortKey, column, desc: true};
            }
            return {...prev, desc: !prev.desc};
        });
        setOpenRowsResetSignal(prev => prev + 1);
    };
    const handleTimeframeChange = (key) => {
        if(key === selectedTimeframe){
            if(key === 'custom') setCustomPickerOpen(true);
            return;
        }
        setSelectedTimeframe(key);
        if(key === 'custom'){
            setCustomPickerOpen(true);
            return;
        }
        setPeriod(rangeForTimeframe(key));
        setPagonationData({currentPageNumber: 0});
        setUpdateAvailable(true);
    };
    if (data==null && error ==null)return<LoadingIcon state={true} height={'10rem'} />;
    const changePeriod = (next) => {
        const dir = next ? 1 : -1;
        setPeriod(({startDate: prevStart, endDate: prevEnd}) => {
            switch(selectedTimeframe){
                case 'weekly': {
                    const s = new Date(prevStart); s.setDate(s.getDate() + dir*7);
                    const e = new Date(s); e.setDate(e.getDate() + 6);
                    return {startDate: s, endDate: e};
                }
                case 'biweekly': {
                    const s = new Date(prevStart); s.setDate(s.getDate() + dir*7);
                    const e = new Date(s); e.setDate(e.getDate() + 13);
                    return {startDate: s, endDate: e};
                }
                case 'monthly': {
                    const s = new Date(prevStart.getFullYear(), prevStart.getMonth() + dir, 1);
                    const e = new Date(s.getFullYear(), s.getMonth()+1, 0);
                    return {startDate: s, endDate: e};
                }
                case 'quarterly': {
                    const s = new Date(prevStart.getFullYear(), prevStart.getMonth() + dir*3, 1);
                    const e = new Date(s.getFullYear(), s.getMonth()+3, 0);
                    return {startDate: s, endDate: e};
                }
                case 'yearly': {
                    const s = new Date(prevStart.getFullYear() + dir, 0, 1);
                    const e = new Date(s.getFullYear(), 11, 31);
                    return {startDate: s, endDate: e};
                }
                case 'custom': {
                    const lengthDays = Math.round((stripTime(prevEnd) - stripTime(prevStart)) / (1000*60*60*24)) + 1;
                    const shift = dir * lengthDays;
                    const s = new Date(prevStart); s.setDate(s.getDate() + shift);
                    const e = new Date(prevEnd); e.setDate(e.getDate() + shift);
                    return {startDate: s, endDate: e};
                }
                default:
                    return {startDate: prevStart, endDate: prevEnd};
            }
        });
        setPagonationData({currentPageNumber: 0});
        setUpdateAvailable(true);
    }
    if (error !== null)return <div className='errorMessage'>{error.customError?error.customError:"Something went wrong"}</div>
    const parentColumns = columnSettings?.target === 'parent';
    const orderedVisibleColumns = parentColumns
        ? orderKeysByCatalog(visibleColumns, columnSettings.catalog)
        : visibleColumns;
    const orderedLoadColumns = columnSettings?.loadColumns
        ? orderKeysByCatalog(loadVisibleColumns, columnSettings.loadColumns.catalog)
        : [];
    const customLoadLayout = columnSettings?.loadColumns ? !isLegacyTruckLoadColumns(orderedLoadColumns) : false;
    const renderedHeader = parentColumns && Array.isArray(headerObject)
        ? orderedVisibleColumns
            .map((key) => headerObject.find((item) => normalizeReportHeader(item).key === key))
            .filter(Boolean)
        : headerObject;
    const columnSource = renderedHeader && renderedHeader.length > 0 ? renderedHeader : headerObject;
    const columnCount = columnSource && columnSource.length > 0 ? columnSource.length : 1;
    const columnWidths = {
        '--col-width': `${100 / columnCount}%`,
        '--last-col-width': `${100 / columnCount - 1}%`
    };
    const periodLabel = dateToPeriodLabel(startDate, false, selectedTimeframe, endDate);
    
    return (
        <div className={styles.mainContainer}>
            <ReportTimeframeSelector
                selectedTimeframe={selectedTimeframe}
                onChange={handleTimeframeChange}
                allowCustomTimeframe={allowCustomTimeframe}
            />
            <div className={styles.controlPanel}>
                {customAction && (!customAction.analyzeDataToShow || customAction.analyzeDataToShow(data)) ? <div className={styles.downloadReportButton} onClick={(e)=>{
                    e.preventDefault();
                    e.stopPropagation();
                    let svgTag = e.target;
                    while (svgTag && typeof svgTag.className === 'string' && !svgTag.className.includes(styles.downloadReportButton)) {
                        svgTag = svgTag.parentNode;
                    }
                    svgTag = svgTag?.querySelector?.('svg');
                    if(!svgTag) return;
                    swapSVGWhileProcessing(
                        () => Promise.resolve(customAction.onClick({
                            data,
                            setData,
                            startDate,
                            endDate,
                            requestReload: () => setUpdateAvailable(true),
                        })),
                        svgTag, null, null, '0.15rem', 'var(--contrast-to-main-color)'
                    );
                }}>
                    <div>
                        {customAction.label}
                    </div>
                </div> : null}
                {(flag('DOWNLOAD_REPORTS') && (itemName || exportToXlsx)) ? <div className={styles.downloadReportButton}>
                    <div onClick={(e)=>{
                        e.preventDefault();
                        e.stopPropagation();
                        let svgTag = e.target;
                        while (svgTag && typeof svgTag.className === 'string' && !svgTag.className.includes(styles.downloadReportButton)) {
                            svgTag = svgTag.parentNode;
                        }
                        svgTag = svgTag?.querySelector?.('svg');
                        swapSVGWhileProcessing((exportToXlsx || downloadExcelFromBackend), svgTag, {reportLink: reportLink, itemType: itemName, fileName: periodLabel, startDate: dateToLocalDateString(startDate), endDate: dateToLocalDateString(endDate), timeframe: selectedTimeframe, subData, visibleColumns: orderedVisibleColumns, catalog: columnSettings?.catalog, loadVisibleColumns: orderedLoadColumns, loadCatalog: columnSettings?.loadColumns?.catalog}, data, '0.15rem', 'var(--contrast-to-main-color)')
                        
                        
                    }}>Download
                        <div className={styles.actionIcon}>
                            <FaRegFileLines />
                        </div>
                    </div>
                </div> : null}
                {columnSettings ? <div className={styles.downloadReportButton}>
                    <div onClick={(e)=>{
                        e.preventDefault();
                        e.stopPropagation();
                        addPopup({
                            content: ReportColumnSettingsPopup,
                            popupData: columnSettings.loadColumns ? {
                                sections: [
                                    {
                                        reportKey: columnSettings.reportKey,
                                        catalog: columnSettings.catalog,
                                        visibleColumns,
                                        sectionTitle: columnSettings.sectionTitle,
                                        sectionHelp: columnSettings.sectionHelp,
                                        onSaved: setVisibleColumns,
                                    },
                                    {
                                        reportKey: columnSettings.loadColumns.reportKey,
                                        catalog: columnSettings.loadColumns.catalog,
                                        visibleColumns: loadVisibleColumns,
                                        sectionTitle: columnSettings.loadColumns.sectionTitle,
                                        sectionHelp: columnSettings.loadColumns.sectionHelp,
                                        onSaved: setLoadVisibleColumns,
                                    },
                                ],
                            } : {
                                reportKey: columnSettings.reportKey,
                                catalog: columnSettings.catalog,
                                visibleColumns,
                                sectionTitle: columnSettings.sectionTitle,
                                sectionHelp: columnSettings.sectionHelp,
                                onSaved: setVisibleColumns,
                            },
                            postSuccessAction: () => {},
                            EZCloseAllowed: true,
                        });
                    }}>Settings
                        <div className={styles.actionIcon}>
                            <IoSettingsSharp />
                        </div>
                    </div>
                </div> : null}
            </div>
            <WeekSelector>
            <FaChevronLeft onClick={()=>{changePeriod(false)}} />
            {selectedTimeframe === 'custom' ? (
                <div ref={customRangeWrapperRef} className={styles.customRangeWrapper}>
                    <button
                        type="button"
                        className={`${styles.customRangeButton}${customPickerOpen ? ' ' + styles.active : ''}`}
                        onClick={()=>setCustomPickerOpen(v=>!v)}
                    >
                        <FaRegCalendarAlt />
                        {periodLabel}
                    </button>
                    {customPickerOpen && (
                        <CalendarPicker
                            selectType="range"
                            initialStart={startDate}
                            initialEnd={endDate}
                            onCancel={()=>setCustomPickerOpen(false)}
                            onApply={(s, e)=>{
                                setPeriod({startDate: s, endDate: e});
                                setPagonationData({currentPageNumber: 0});
                                setUpdateAvailable(true);
                                setCustomPickerOpen(false);
                            }}
                        />
                    )}
                </div>
            ) : (
                <span className="non-selectable">{periodLabel}</span>
            )}
            <FaChevronRight onClick={()=>{changePeriod(true)}} />
        </WeekSelector>
            {data.length < 1 ? (
                <div className={styles.noData}>
                    No Data For Selected Period
                </div>
            ) : (
        <>
        
            {customTableSummary ? <div className={styles.tableSummary}>{customTableSummary(data, subData)}</div> : null}

            {predefinedListConf ? (
                <div className={styles.listReportContainer}>
                    <ListView listRef={null} ListConf={predefinedListConf} Data={data} Error={error} NoFooter={true} RequestReload={()=>{}} />
                </div>
            ) : (
                <div className={styles.reportTableWrapper}>
                    {columnSource ? <ReportHeaderObject headerObject={columnSource} headerStyles={columnWidths} sortingAllowed={sortingAllowed} sortingData={sortingData} onSort={handleReportSort} /> : null}
                    {sortedData.map((report, index) => {
                        if (rowType === "clickable") {
                            return <OpenableRowObject key={getReportRowKey(report, index)} rowType={rowType} report={report} selectedPeriod={startDate} department={department} custoMreportClass={custoMreportClass} rowFunction={rowFunction} SubContentPart={SubContentPart} columnWidths={columnWidths} subcontentArrayName={subcontentArrayName} openRowsResetSignal={openRowsResetSignal} visibleColumns={parentColumns ? undefined : visibleColumns} catalog={parentColumns ? undefined : columnSettings?.catalog} columnKeys={parentColumns ? columnSource.map((item) => normalizeReportHeader(item).key) : null} loadVisibleColumns={orderedLoadColumns} loadCatalog={columnSettings?.loadColumns?.catalog} customLoadLayout={customLoadLayout} />;
                        }
                        if (rowType === "other") {
                            return <RowObject key={getReportRowKey(report, index)} report={report} />;
                        }
                        return null;
                    })}
                </div>
            )}
        </>
            )}
            <ListViewFooter paginationData={{
                data: pagonationData,
                setPaginationData: setPagonationData
            }} resultCount={data.length} requestReload={()=>{setUpdateAvailable(true);}} />
        </div>
    );
}
async function downloadExcelFromBackend(data) {
    const params = new URLSearchParams({
        timeframe: data.timeframe,
        startDate: data.startDate,
        endDate: data.endDate,
    });
    let filename = data.itemType+` Report ${data.fileName.replace(/\//g, '.')}`;
    if(data.timeframe === 'monthly'){
        filename += ` ${data.startDate.slice(0, 4)}`;
    }
    filename += '.xlsx';

    return new Promise((resolve) => {
        sendRequest(
            'GET',
            `${API_ROOT}${data.reportLink}/export?${params.toString()}`,
            null,
            async (response) => {
                try {
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    await new Promise(res => setTimeout(res, 100));
                    URL.revokeObjectURL(url);
                    resolve(true);
                } catch (err) {
                    console.error("Failed to export XLSX:", err);
                    resolve(false);
                }
            },
            (err) => {
                console.error('Failed to download report:', err);
                resolve(false);
            },
            null,
            null,
            null,
            true,
        );
    });
}
const ReportHeaderObject = ({headerObject, headerStyles, sortingAllowed, sortingData, onSort}) => {
    return <div className={styles.reportHeader} style={headerStyles}>
        {headerObject.map((item, index) => {
            const headerItem = normalizeReportHeader(item);
            const sortKey = headerItem.rawValKey || headerItem.key || headerItem.label;
            const canSortByHeader = typeof item !== 'string' && headerItem.sortable !== false && (headerItem.rawValKey || headerItem.key || headerItem.methodToObtainRawVal);
            const sortable = sortingAllowed && canSortByHeader;
            const active = sortingData?.key === sortKey;
            return <div
                key={sortKey || index}
                className={sortable ? styles.sortableHeader : ''}
                onClick={sortable ? () => onSort(headerItem) : undefined}
            >
                <span>{headerItem.label}</span>
                {sortable ? <span className={styles.sortIcon}>{active && !sortingData.desc ? <HiArrowNarrowDown /> : <HiArrowNarrowUp />}</span> : null}
            </div>
        })}
    </div>
}
const RowObject = ({ report }) => (
    <div className={styles.simpleRow}>
        <div>{report.teamName}</div>
        <div><TotalGrossContainer totalGross={report.totalGross} /></div>
    </div>
);
const OpenableRowObject = ({rowType, report, selectedPeriod, department, custoMreportClass, rowFunction, SubContentPart, columnWidths, subcontentArrayName, openRowsResetSignal, visibleColumns, catalog, columnKeys, loadVisibleColumns, loadCatalog, customLoadLayout }) => {
    const [currentReport, setCurrentReport] = useState(report)
    const [open, setOpen] = useState(false);
    useEffect(() => {
        setCurrentReport(report);
        setOpen(false);
    }, [report, openRowsResetSignal]);
    const ClickOnReport = (e) => {
        try {
            let target = e.target;
            
            while(target.getAttribute("data-state") === null){
                target = target.parentNode;
            }
            if(target.getAttribute("data-state") === "closed"){
                setOpen(true);
            }else{
                setOpen(false);
            }
        } catch (error) {
            console.log(error);
        }
    }
    let singleReport = custoMreportClass ? new custoMreportClass(currentReport) : currentReport;
    const openable = currentReport[subcontentArrayName]?.length > 0;
    const rowKeys = Array.isArray(columnKeys) && columnKeys.length > 0 ? columnKeys : Object.keys(singleReport);
    return (
        <div data-state={open ? "open" : "closed"} className={`${styles.clickableReport} ${!openable ? styles.notOpanble : ""} ${open ? styles.opened : ""}`}>
            <div
                onClick={(e) => openable && ClickOnReport(e)}
                className={`${styles.reportRow} ${openable ? styles.clickable : ""}`}
                style={columnWidths}
            >
                {rowKeys.map((key, index) =>
                    rowFunction(key, singleReport, currentReport, selectedPeriod, setCurrentReport, department, index === rowKeys.length - 1)
                )}
                <span>
                    <FaChevronDown style={{ display: open ? "none" : "" }} />
                    <FaChevronUp style={{ display: !open ? "none" : "" }} />
                </span>
            </div>
            {open && (
                <div id="reportDetails" className={`${styles.subContent}${catalog || customLoadLayout ? ` ${styles.columnCustom}` : ''}`}>
                    <SubContentPart currentReport={currentReport} visibleColumns={visibleColumns} catalog={catalog} loadVisibleColumns={loadVisibleColumns} loadCatalog={loadCatalog} customLoadLayout={customLoadLayout} />
                </div>
            )}
        </div>
    );
}

export const TotalGrossContainer = ({totalGross}) => {
    if(totalGross === '-')return "-";
    const colorObj = GrossColorGenerator(totalGross);
    return <span style={{color:`rgb(${colorObj.red}, ${colorObj.green}, ${colorObj.blue})`}}>{priceOnFinish(`${totalGross}`, true)}</span>
}
export const RPMContainer = ({rpm}) => {
    if(rpm === '-')return "-";
    const colorObj = RPMColorGenerator(parseFloat(rpm));
    return <span style={{color:`rgb(${colorObj.red}, ${colorObj.green}, ${colorObj.blue})`}}>{priceOnFinish(`${rpm}`) }</span>
}
export function getCurrentWeek(firstDayOfAWeek, previousWeek){
    const weekDays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const intRepresentation = weekDays.indexOf(firstDayOfAWeek.toLowerCase())
    const result = new Date();
    if(previousWeek){
        result.setDate(result.getDate() - 7);
    }
    const currentDay = result.getDay(); // getDay() returns a number: 0 (Sunday) to 6 (Saturday)
    const daysBack = (currentDay - intRepresentation + 7) % 7;
    result.setDate(result.getDate() - daysBack);
    return result;
}
export function dateToLocalDateString(date){
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${(date.getDate()).toString().padStart(2, "0")}`;
}
export function dateStringToLocalDateString(dateString){
    if (!dateString || typeof dateString !== 'string') return dateString;
    const trimmed = dateString.trim();
    const isoMatch = trimmed.match(/\d{4}-\d{2}-\d{2}/);
    if (isoMatch) return isoMatch[0]; // already ISO (Java LocalDate) or extract from malformed string
    const parts = trimmed.split('/');
    if (parts.length < 3 || !parts[0] || !parts[1] || !parts[2]) return dateString;
    return `${'20' + parts[2]}-${parts[0]}-${parts[1]}`;
}
export function dateToPeriodLabel(date, withYear, timeframe, endDate){
    if(timeframe === 'monthly'){
        return date.toLocaleDateString("en-US",{ month: 'long' });
    }
    if(timeframe === 'quarterly'){
        return `${date.getFullYear()} - Q${Math.floor(date.getMonth() / 3) + 1}`;
    }
    if(timeframe === 'yearly'){
        return `${date.getFullYear()}`;
    }
    // weekly, biweekly, custom (or no timeframe given) → e.g. Mar 28 - Apr 3
    let actualEnd = endDate;
    if(!actualEnd){
        actualEnd = new Date(date);
        actualEnd.setDate(date.getDate() + (timeframe === 'biweekly' ? 13 : 6));
    }
    const currentYear = new Date().getFullYear();
    const outsideCurrentYear =
        date.getFullYear() !== currentYear || actualEnd.getFullYear() !== currentYear;
    const crossesCalendarYears = date.getFullYear() !== actualEnd.getFullYear();
    const needYear = !!withYear || crossesCalendarYears || outsideCurrentYear;
    const opts = outsideCurrentYear
        ? { month: 'long', day: 'numeric', year: 'numeric' }
        : needYear
          ? { month: 'short', day: 'numeric', year: 'numeric' }
          : { month: 'short', day: 'numeric' };
    return `${date.toLocaleDateString('en-US', opts)} - ${actualEnd.toLocaleDateString('en-US', opts)}`;
}